import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
    "Qual regime tributário é melhor para mim?",
    "Como abro um MEI?",
    "Vocês atendem fora de São Paulo?",
];

const WELCOME = {
    role: "assistant",
    content:
        "Olá! Sou o assistente virtual da WBS Serviços Contábeis. Posso tirar suas dúvidas sobre MEI, Simples Nacional, Lucro Presumido e Lucro Real. Como posso ajudar?",
};

const getSessionId = () => {
    let id = localStorage.getItem("wbs_chat_session");
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("wbs_chat_session", id);
    }
    return id;
};

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([WELCOME]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const listRef = useRef(null);
    const sessionId = useRef(getSessionId());

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, open]);

    const sendMessage = async (text) => {
        const message = (text ?? input).trim();
        if (!message || loading) return;
        setInput("");
        setLoading(true);
        setMessages((prev) => [...prev, { role: "user", content: message }, { role: "assistant", content: "" }]);

        try {
            const res = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).trim();
                    if (payload === "[DONE]") continue;
                    try {
                        const obj = JSON.parse(payload);
                        if (obj.content) {
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = {
                                    role: "assistant",
                                    content: next[next.length - 1].content + obj.content,
                                };
                                return next;
                            });
                        }
                        if (obj.error) {
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = {
                                    role: "assistant",
                                    content: "Desculpe, tive um problema. Chame a gente no WhatsApp (11) 93962-3265.",
                                };
                                return next;
                            });
                        }
                    } catch (e) { /* ignora pacote incompleto */ }
                }
            }
        } catch (err) {
            setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                    role: "assistant",
                    content: "Não consegui responder agora. Fale com a WBS no WhatsApp (11) 93962-3265.",
                };
                return next;
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {!open && (
                <button
                    data-testid="chat-open-button"
                    onClick={() => {
                        setOpen(true);
                        track("interaction", { label: "chat-open", path: window.location.pathname });
                    }}
                    aria-label="Abrir assistente virtual"
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-slate-900 text-white pl-4 pr-5 py-3.5 shadow-lg transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
                >
                    <MessageCircle className="w-5 h-5 text-amber-400" />
                    <span className="text-sm font-bold">Fale com a WBS</span>
                </button>
            )}

            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] max-h-[600px] flex flex-col bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-fade-up"
                >
                    <div className="flex items-center justify-between bg-slate-900 px-5 py-4">
                        <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/20 text-amber-400">
                                <Sparkles className="w-4 h-4" />
                            </span>
                            <div>
                                <p className="text-sm font-bold text-white">Assistente WBS</p>
                                <p className="text-[11px] text-slate-400">Tire dúvidas sobre regimes tributários</p>
                            </div>
                        </div>
                        <button
                            data-testid="chat-close-button"
                            onClick={() => setOpen(false)}
                            aria-label="Fechar assistente"
                            className="p-1.5 text-slate-400 hover:text-white transition-colors duration-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-[280px] max-h-[380px] bg-[#F8FAFC]" data-testid="chat-messages">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                data-testid={`chat-message-${msg.role}-${idx}`}
                                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                                        msg.role === "user"
                                            ? "bg-amber-500 text-slate-900 font-medium"
                                            : "bg-white border border-slate-200 text-slate-700"
                                    }`}
                                >
                                    {msg.content || (loading && idx === messages.length - 1 ? "..." : "")}
                                </div>
                            </div>
                        ))}
                    </div>

                    {messages.length <= 1 && (
                        <div className="px-5 pb-2 pt-3 bg-[#F8FAFC] flex flex-wrap gap-2" data-testid="chat-suggestions">
                            {SUGGESTIONS.map((s, i) => (
                                <button
                                    key={s}
                                    data-testid={`chat-suggestion-${i}`}
                                    onClick={() => sendMessage(s)}
                                    className="text-xs font-semibold text-blue-900 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 hover:bg-blue-100 transition-colors duration-200"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            sendMessage();
                        }}
                        className="flex items-center gap-2 border-t border-slate-200 bg-white px-4 py-3"
                        data-testid="chat-form"
                    >
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Digite sua dúvida..."
                            maxLength={1000}
                            className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400 text-slate-800"
                        />
                        <Button
                            type="submit"
                            size="icon"
                            disabled={loading || !input.trim()}
                            data-testid="chat-send-button"
                            className="rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 shrink-0"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </form>
                </div>
            )}
        </>
    );
};
